import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Sortable from "sortablejs";
import useClickOutside from "../../../../hooks/useClickOutside";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";
import Modal from "../../../../parts/modal-new";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import {
  getRule,
  rankedRuleKeys,
  repMaxRuleKey,
  ruleWarning,
  WINDOWS,
} from "../leaderboard-rules";

// How each member-board column aggregates (fixed by the metric).
const AGGREGATIONS = {
  workouts: ["challengeReport.rules.agg.total", "Total"],
  totalVolume: ["challengeReport.rules.agg.total", "Total"],
  totalReps: ["challengeReport.rules.agg.total", "Total"],
  totalSets: ["challengeReport.rules.agg.total", "Total"],
  completionRate: ["challengeReport.rules.agg.percentage", "Percentage"],
  habitsAdherence: ["challengeReport.rules.agg.percentage", "Percentage"],
  avgDailySteps: ["challengeReport.rules.agg.dailyAverage", "Daily average"],
  avgWaterIntake: ["challengeReport.rules.agg.dailyAverage", "Daily average"],
  avgSessionRpe: ["challengeReport.rules.agg.average", "Average"],
  avgTrainingReadiness: ["challengeReport.rules.agg.average", "Average"],
  bodyweight: ["challengeReport.rules.agg.change", "Change"],
};
const aggregationOf = (key) =>
  AGGREGATIONS[key] ||
  (key.startsWith("rm:")
    ? ["challengeReport.rules.agg.heaviest", "Heaviest load"]
    : ["challengeReport.rules.agg.change", "Change"]);

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ProgramMultiSelect — dropdown to add, chips with × to remove
// ---------------------------------------------------------------------------

const ProgramMultiSelect = ({
  options,
  selected,
  onToggle,
  placeholder,
  allSelectedLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const unselected = options.filter((n) => !selected.includes(n));

  useClickOutside(
    dropdownRef,
    () => setOpen(false),
    open,
    [triggerRef.current].filter(Boolean)
  );

  const handleOpen = () => {
    if (unselected.length === 0) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setOpen((o) => !o);
  };

  return (
    <div className="mt-2">
      <button
        ref={triggerRef}
        type="button"
        data-button-type="secondary"
        className="w-full flex items-center justify-between text-sm!"
        onClick={handleOpen}
        disabled={unselected.length === 0}
      >
        <span className="opacity-60">
          {unselected.length === 0 ? allSelectedLabel : placeholder}
        </span>
        {unselected.length > 0 && (
          <SvgIcon
            src={open ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"}
            alt=""
            classNames="ml-2 w-5 h-5"
          />
        )}
      </button>

      {open && unselected.length > 0 && (
        <ul
          ref={dropdownRef}
          className="py-1 border border-(--white-10) rounded-(--border-radius) shadow-lg max-h-48 overflow-y-auto list-none! p-1! m-0!"
          style={{
            ...dropdownStyle,
            backgroundColor: "var(--dropdown-background-color)",
          }}
        >
          {unselected.map((name) => (
            <li key={name} className="m-0!">
              <button
                type="button"
                className="w-full justify-start px-3 py-2 text-sm! rounded-(--border-radius) hover:bg-(--white-05) transition-colors font-normal!"
                onClick={() => {
                  onToggle(name);
                  setOpen(false);
                }}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.map((name) => (
            <button
              key={name}
              type="button"
              data-button-type="secondary"
              sz="small"
              className="flex items-center gap-1 text-xs px-2!"
              onClick={() => onToggle(name)}
            >
              {name}
              <span
                aria-hidden="true"
                className="opacity-70 text-sm leading-none ml-0.5 text-(--danger)"
              >
                ×
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DEFAULT_CHECKED_COLUMNS = new Set([
  "completionRate",
  "workouts",
  "programTeam",
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A two-column checkbox grid row */
const CheckboxGrid = ({
  items,
  checkedSet,
  onToggle,
  disabled = false,
  cols = 2,
}) => (
  <ul
    className="customize-table-modal__grid"
    style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
  >
    {items.map(({ key, label }) => (
      <li key={key}>
        <label
          className={`flex! items-center gap-1 opacity-100! !mb-0${disabled ? " customize-table-modal__label--disabled" : ""}`}
        >
          <input
            type="checkbox"
            checked={disabled ? true : checkedSet.has(key)}
            onChange={() => !disabled && onToggle(key)}
            disabled={disabled}
            className="shrink-0"
          />
          {label}
        </label>
      </li>
    ))}
  </ul>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CustomizeTableModal = ({
  isOpen,
  onClose,
  selectedColumns,
  selectedMeasurements,
  programOptions = [],
  onApply = () => {},
  onSaveTemplate = () => {},
  initialName = "",
  initialScopeTeamPrograms = [],
  initialRepMaxExercises = [],
  initialIsDefault = false,
  initialColumnRules = [],
  liveBoardCount = 0,
  existingId,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const { activeUserToRequest } = locals;
  const exerciseLibrary = activeUserToRequest?.exerciseLibrary ?? {};

  const trainingMetrics = useMemo(
    () => [
      {
        key: "totalVolume",
        label: t(
          "challengeReport.customize.totalVolume",
          "Total volume (weight lifted)"
        ),
      },
      {
        key: "avgSessionRpe",
        label: t("challengeReport.customize.avgSessionRpe", "Avg session RPE"),
      },
      {
        key: "totalSets",
        label: t("challengeReport.customize.totalSets", "Total volume (sets)"),
      },
      {
        key: "totalReps",
        label: t("challengeReport.customize.totalReps", "Total reps"),
      },
      {
        key: "avgTrainingReadiness",
        label: t(
          "challengeReport.customize.avgTrainingReadiness",
          "Avg training readiness"
        ),
      },
      {
        key: "photos",
        label: (
          <>
            {t("challengeReport.customize.photos", "Progress photos")}
            <span className="customize-table-modal__coach-only">
              {t("challengeReport.rules.coachOnly", "coach only")}
            </span>
          </>
        ),
      },
    ],
    [t]
  );

  const defaultColumns = useMemo(
    () => [
      {
        key: "completionRate",
        label: t("challengeReport.customize.completionRate", "Completion %"),
      },
      {
        key: "workouts",
        label: t(
          "challengeReport.customize.workoutsCompleted",
          "Workouts completed"
        ),
      },
      {
        key: "programTeam",
        label: t("challengeReport.customize.programTeam", "Program / Team"),
      },
    ],
    [t]
  );

  const lifestyleWellness = useMemo(
    () => [
      {
        key: "habitsAdherence",
        label: t(
          "challengeReport.customize.habitsAdherence",
          "Habits adherence %"
        ),
      },
      {
        key: "avgWaterIntake",
        label: t(
          "challengeReport.customize.avgWaterIntake",
          "Avg water intake"
        ),
      },
      {
        key: "avgDailySteps",
        label: t("challengeReport.customize.avgDailySteps", "Avg steps"),
      },
      {
        key: "bodyweight",
        label: t("challengeReport.customize.bodyweight", "Bodyweight change"),
      },
    ],
    [t]
  );

  const bodyMeasurements = useMemo(
    () => [
      { key: "neck", label: t("challengeReport.customize.neck", "Neck") },
      { key: "chest", label: t("challengeReport.customize.chest", "Chest") },
      {
        key: "leftArm",
        label: t("challengeReport.customize.leftArm", "Left arm"),
      },
      {
        key: "rightArm",
        label: t("challengeReport.customize.rightArm", "Right arm"),
      },
      { key: "waist", label: t("challengeReport.customize.waist", "Waist") },
      { key: "hips", label: t("challengeReport.customize.hips", "Hips") },
      {
        key: "leftThigh",
        label: t("challengeReport.customize.leftThigh", "Left thigh"),
      },
      {
        key: "rightThigh",
        label: t("challengeReport.customize.rightThigh", "Right thigh"),
      },
      {
        key: "leftCalf",
        label: t("challengeReport.customize.leftCalf", "Left calf"),
      },
      {
        key: "rightCalf",
        label: t("challengeReport.customize.rightCalf", "Right calf"),
      },
      { key: "glutes", label: t("challengeReport.customize.glutes", "Glutes") },
    ],
    [t]
  );

  // Build a flat list of all exercises from the library for the SearchDropdown
  const exerciseOptions = useMemo(() => {
    const options = [];
    Object.entries(exerciseLibrary).forEach(([primary, movements]) => {
      if (!["assistant", "other"].includes(primary)) {
        options.push({
          movementLabel: getExerciseName({
            type: "primary",
            secondary: null,
            primary,
          }),
          value: primary,
          primary,
          type: "primary",
          secondary: null,
        });
      }
      movements.forEach((item) => {
        const type = primary === "assistant" ? "assistant" : "secondary";
        options.push({
          movementLabel: getExerciseName({
            type,
            secondary: item.movement,
            primary,
          }),
          value: `${primary}${item.movement}`,
          primary,
          type,
          secondary: item.movement,
        });
      });
    });
    return options;
  }, [exerciseLibrary]);

  const [localColumns, setLocalColumns] = useState(
    () => new Set(selectedColumns)
  );
  const [localMeasurements, setLocalMeasurements] = useState(
    () => new Set(selectedMeasurements)
  );
  const [localScopeTeamPrograms, setLocalScopeTeamPrograms] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [asDefault, setAsDefault] = useState(initialIsDefault);
  const [localRepMaxExercises, setLocalRepMaxExercises] = useState(() =>
    initialRepMaxExercises.map((x) => ({
      ...x,
      _uid: x._uid ?? crypto.randomUUID(),
    }))
  );
  const [pendingExercise, setPendingExercise] = useState(null);
  const [pendingRepTarget, setPendingRepTarget] = useState(5);
  const [localRules, setLocalRules] = useState(initialColumnRules);

  // Reset local state whenever the modal opens
  // biome-ignore lint/correctness/useExhaustiveDependencies: <we only want to reset when isOpen changes>
  useEffect(() => {
    if (isOpen) {
      setLocalColumns(new Set(selectedColumns));
      setLocalMeasurements(new Set(selectedMeasurements));
      setLocalScopeTeamPrograms(initialScopeTeamPrograms);
      setTemplateName(initialName);
      setAsDefault(initialIsDefault);
      setLocalRepMaxExercises(
        initialRepMaxExercises.map((x) => ({
          ...x,
          _uid: x._uid ?? crypto.randomUUID(),
        }))
      );
      setPendingExercise(null);
      setPendingRepTarget(5);
      setLocalRules(initialColumnRules);
    }
  }, [isOpen]);

  // Columns members are ranked on, with their rules.
  const rankedKeys = rankedRuleKeys({
    columns: localColumns,
    bodyMeasurements: localMeasurements,
    repMaxExercises: localRepMaxExercises,
  });
  const updateRule = (key, field, value) =>
    setLocalRules((prev) => [
      ...prev.filter((r) => r.key !== key),
      { ...getRule(prev, key), [field]: value },
    ]);

  const ruleLabel = (key) => {
    if (key.startsWith("rm:")) {
      const exercise = localRepMaxExercises.find(
        (x) => repMaxRuleKey(x) === key
      );
      return `${exercise?.label || key.split(":")[1]} ${exercise?.repTarget}RM`;
    }
    if (key.startsWith("bm:")) {
      return bodyMeasurements.find((m) => m.key === key.slice(3))?.label;
    }
    return [...defaultColumns, ...trainingMetrics, ...lifestyleWellness].find(
      (c) => c.key === key
    )?.label;
  };

  const windowLabel = (window) =>
    ({
      follow: t("challengeReport.rules.window.follow", "Follow table range"),
      365: t("challengeReport.rules.window.365", "Last 12 months"),
    })[window] ||
    t("challengeReport.rules.window.days", "Last {{days}} days", {
      days: window,
    });

  // -------------------------------------------------------------------------
  // Toggle helpers
  // -------------------------------------------------------------------------

  const toggleColumn = (key) =>
    setLocalColumns((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleMeasurement = (key) =>
    setLocalMeasurements((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleScopeProgram = (name) =>
    setLocalScopeTeamPrograms((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  // -------------------------------------------------------------------------
  // Rep-max handlers
  // -------------------------------------------------------------------------

  const handleAddRepMax = () => {
    if (!pendingExercise) return;
    setLocalRepMaxExercises((prev) => [
      ...prev,
      {
        type: pendingExercise.type,
        primary: pendingExercise.primary,
        secondary: pendingExercise.secondary || null,
        repTarget: pendingRepTarget,
        label: pendingExercise.movementLabel,
        _uid: crypto.randomUUID(),
      },
    ]);
    setPendingExercise(null);
  };

  const removeRepMax = (index) =>
    setLocalRepMaxExercises((prev) => prev.filter((_, i) => i !== index));

  const repMaxListRef = useRef(null);

  // Sortable drag-and-drop for rep-max exercise ordering
  useEffect(() => {
    if (!isOpen || !repMaxListRef.current) return;
    const dragImage = document.createElement("img");
    dragImage.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    const sortable = Sortable.create(repMaxListRef.current, {
      animation: 200,
      handle: ".rep-max-grabber",
      draggable: ".rep-max-item",
      setData(dataTransfer) {
        dataTransfer.setDragImage(dragImage, 0, 0);
      },
      onEnd(e) {
        if (e.oldIndex === e.newIndex) return;
        // Revert DOM — let React own the list
        const items = e.from.querySelectorAll(":scope > .rep-max-item");
        e.from.insertBefore(
          e.item,
          items[e.oldIndex + (e.oldIndex > e.newIndex)]
        );
        setLocalRepMaxExercises((prev) => {
          const next = [...prev];
          const [moved] = next.splice(e.oldIndex, 1);
          next.splice(e.newIndex, 0, moved);
          return next;
        });
      },
    });
    return () => {
      sortable.destroy();
      dragImage.remove();
    };
  }, [isOpen]);

  const updateRepTarget = (index, value) =>
    setLocalRepMaxExercises((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, repTarget: value } : item
      )
    );

  // -------------------------------------------------------------------------
  // Submit handlers
  // -------------------------------------------------------------------------

  const handleSave = () => {
    const repMaxExercises = localRepMaxExercises.map(
      ({ _uid, ...rest }) => rest
    );
    const removedRankedColumns = rankedRuleKeys({
      columns: selectedColumns,
      bodyMeasurements: selectedMeasurements,
      repMaxExercises: initialRepMaxExercises,
    }).filter((key) => !rankedKeys.includes(key));
    if (
      existingId &&
      liveBoardCount > 0 &&
      removedRankedColumns.length > 0 &&
      !confirm(
        t(
          "challengeReport.rules.confirmRemove",
          "Live leaderboards rank people on {{columns}}. Removing it takes it off {{count}} live leaderboards straight away. Continue?",
          {
            columns: removedRankedColumns.map(ruleLabel).join(", "),
            count: liveBoardCount,
          }
        )
      )
    ) {
      return;
    }
    const columnRules = rankedKeys.map((key) => getRule(localRules, key));
    if (templateName.trim()) {
      onSaveTemplate({
        name: templateName.trim(),
        columns: localColumns,
        measurements: localMeasurements,
        scopeTeamPrograms: localScopeTeamPrograms,
        repMaxExercises,
        columnRules,
        isDefault: asDefault,
        existingId,
      });
    } else {
      onApply({
        columns: localColumns,
        measurements: localMeasurements,
        scopeTeamPrograms: localScopeTeamPrograms,
        repMaxExercises,
        columnRules,
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  const rulesPanel = (
    <div className="customize-table-modal__rules">
      <div className="customize-table-modal__rules-head">
        <h3 className="customize-table-modal__section-header">
          {t("challengeReport.rules.title", "How each metric is ranked")}
        </h3>
        <p className="customize-table-modal__section-desc">
          {t(
            "challengeReport.rules.desc",
            "Applies to public leaderboards. Columns on “Follow table range” follow your date range here and rank people in the app on the last 12 months."
          )}
        </p>
        {rankedKeys.some(
          (key) => getRule(localRules, key).window === "follow"
        ) && (
          <div className="customize-table-modal__rules-notice">
            <p>
              {t(
                "challengeReport.rules.noWindowTitle",
                "No window set · Public leaderboards only"
              )}
            </p>
            {t(
              "challengeReport.rules.noWindowBody",
              "Columns marked “Set a window” rank people in the app on the last 12 months. Pick a time window to rank on a specific period instead."
            )}
          </div>
        )}
      </div>
      {rankedKeys.length === 0 && (
        <p className="customize-table-modal__empty">
          {t("challengeReport.rules.empty", "Tick a metric to give it a rule.")}
        </p>
      )}
      {rankedKeys.map((key) => {
        const rule = getRule(localRules, key);
        const [aggKey, aggFallback] = aggregationOf(key);
        const warning = ruleWarning(rule);
        return (
          <div key={key} className="customize-table-modal__rule">
            <p className="customize-table-modal__rule-name">
              {ruleLabel(key)}
              {rule.window === "follow" ? (
                <span
                  className="customize-table-modal__rule-chip"
                  data-state="unset"
                >
                  {t("challengeReport.rules.setWindow", "Set a window")}
                </span>
              ) : (
                <span
                  className="customize-table-modal__rule-chip"
                  data-state="done"
                >
                  {t("challengeReport.rules.done", "Done")}
                </span>
              )}
            </p>
            <div className="customize-table-modal__rule-selects">
              <span>{t(aggKey, aggFallback)}</span>
              <select
                data-unset={rule.window === "follow" || undefined}
                value={rule.window}
                onChange={(e) => updateRule(key, "window", e.target.value)}
              >
                {WINDOWS.map((window) => (
                  <option key={window} value={window}>
                    {windowLabel(window)}
                  </option>
                ))}
              </select>
              <select
                value={rule.direction}
                onChange={(e) => updateRule(key, "direction", e.target.value)}
              >
                <option value="desc">
                  {t("challengeReport.rules.desc_", "Higher ranks first")}
                </option>
                <option value="asc">
                  {t("challengeReport.rules.asc", "Lower ranks first")}
                </option>
              </select>
            </div>
            <p className="customize-table-modal__rule-caption">
              {ruleLabel(key)} · {t(aggKey, aggFallback).toLowerCase()} ·{" "}
              {rule.window === "follow"
                ? t(
                    "challengeReport.rules.followCaption",
                    "your table range → last 12 months in the app"
                  )
                : windowLabel(rule.window).toLowerCase()}
            </p>
            {warning === "tenure" && (
              <p className="customize-table-modal__rule-warning">
                {t(
                  "challengeReport.rules.tenureWarning",
                  "Whoever joined first will win this. A total over a long window rewards tenure, not effort — someone who joined last month can't catch up. A shorter window ranks the same behaviour without the head start."
                )}
              </p>
            )}
            {warning === "shortLift" && (
              <p className="customize-table-modal__rule-warning">
                {t(
                  "challengeReport.rules.shortLiftWarning",
                  "Only people who tested recently will rank. Anyone whose heavy set falls outside this window drops off the board. Lifts work best over 60 or 90 days."
                )}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Modal
      title={t("challengeReport.customize.title", "Customize table")}
      className="customize-table-modal"
      aside={rulesPanel}
      onClose={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Scrollable body                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div className="body customize-table-modal__body max-w-200">
        <div className="customize-table-modal__metrics">
          {liveBoardCount > 0 && (
            <p className="customize-table-modal__live-banner">
              {t(
                "challengeReport.rules.liveBanner",
                "Live on {{count}} leaderboards — saving updates what people see in the app straight away.",
                { count: liveBoardCount }
              )}
            </p>
          )}
          <label htmlFor="template-name">
            {t(
              "challengeReport.customize.templateNamePlaceholder",
              "Template name"
            )}
          </label>
          <input
            type="text"
            className="customize-table-modal__template-name"
            placeholder={t(
              "challengeReport.customize.templateNamePlaceholder",
              "Template name"
            )}
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            id="template-name"
            required
          />

          {/* -------- 1. DEFAULT (always-on) -------- */}
          <section className="customize-table-modal__section">
            <h3 className="customize-table-modal__section-header capitalize">
              {t("challengeReport.customize.sectionDefault", "DEFAULT")}
            </h3>
            <CheckboxGrid
              items={defaultColumns}
              checkedSet={DEFAULT_CHECKED_COLUMNS}
              onToggle={() => {}}
              disabled
              cols={3}
            />
          </section>

          {/* -------- 2. TRAINING METRICS -------- */}
          <section className="customize-table-modal__section">
            <h3 className="customize-table-modal__section-header capitalize">
              {t(
                "challengeReport.customize.sectionTrainingMetrics",
                "TRAINING METRICS"
              )}
            </h3>
            <CheckboxGrid
              items={trainingMetrics}
              checkedSet={localColumns}
              onToggle={toggleColumn}
              cols={4}
            />
          </section>

          {/* -------- 3. LIFESTYLE & WELLNESS -------- */}
          <section className="customize-table-modal__section">
            <h3 className="customize-table-modal__section-header capitalize">
              {t(
                "challengeReport.customize.sectionLifestyle",
                "LIFESTYLE & WELLNESS"
              )}
            </h3>
            <CheckboxGrid
              items={lifestyleWellness}
              checkedSet={localColumns}
              onToggle={toggleColumn}
              cols={4}
            />
          </section>

          {/* -------- 4. BODY MEASUREMENT CHANGE -------- */}
          <section className="customize-table-modal__section">
            <h3 className="customize-table-modal__section-header capitalize">
              {t(
                "challengeReport.customize.sectionBodyMeasurement",
                "BODY MEASUREMENT CHANGE"
              )}
            </h3>
            <p className="customize-table-modal__section-desc">
              {t(
                "challengeReport.customize.bodyMeasurementDesc",
                "Select which body parts to include. Each becomes its own column showing before / after change."
              )}
            </p>
            <CheckboxGrid
              items={bodyMeasurements}
              checkedSet={localMeasurements}
              onToggle={toggleMeasurement}
              cols={6}
            />
          </section>

          {/* -------- 5. REP MAX IMPROVEMENT -------- */}
          <section className="customize-table-modal__section">
            <h3 className="customize-table-modal__section-header capitalize">
              {t(
                "challengeReport.customize.sectionRepMax",
                "REP MAX IMPROVEMENT"
              )}
            </h3>
            <p className="customize-table-modal__section-desc">
              {t(
                "challengeReport.customize.repMaxDesc",
                "Add exercises to track before/after rep-max improvement. Each becomes its own column."
              )}
            </p>

            {/* Picker row: exercise dropdown + rep target + add button */}
            <div className="flex items-center gap-2 mt-3">
              <div className="flex-1">
                <SearchDropdown
                  handleChange={(option) => setPendingExercise(option)}
                  options={exerciseOptions}
                  valueKey="movementLabel"
                  value={pendingExercise?.movementLabel || ""}
                  placeholder={t(
                    "challengeReport.customize.repMaxPickerPlaceholder",
                    "Search exercise..."
                  )}
                />
              </div>
              <select
                value={pendingRepTarget}
                onChange={(e) => setPendingRepTarget(Number(e.target.value))}
                className="w-auto h-10"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                  <option key={n} value={n}>
                    {n}RM
                  </option>
                ))}
              </select>
              <button
                type="button"
                data-button-type="secondary"
                onClick={handleAddRepMax}
                disabled={!pendingExercise}
              >
                {t("challengeReport.customize.addExercise", "Add")}
              </button>
            </div>

            {/* Added exercises list */}
            <ul ref={repMaxListRef} className="mt-3 flex flex-col gap-2">
              {localRepMaxExercises.map((item, i) => (
                <li
                  key={item._uid}
                  className="rep-max-item flex items-center gap-2"
                >
                  <SvgIcon
                    src="/img/new-icons/menu.svg"
                    classNames="rep-max-grabber cursor-grab"
                  />
                  <div className="flex-1 flex items-center gap-4">
                    <select
                      value={item.repTarget}
                      onChange={(e) =>
                        updateRepTarget(i, Number(e.target.value))
                      }
                      className="w-auto"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                        <option key={n} value={n}>
                          {n}RM
                        </option>
                      ))}
                    </select>
                    <span>{item.label}</span>
                  </div>
                  <button
                    type="button"
                    data-button-type="secondary"
                    onClick={() => removeRepMax(i)}
                    aria-label="Remove"
                    data-action="cancel"
                    data-icon-only
                  >
                    <img src="/img/new-icons/red-cross.svg" alt="dismiss" />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {/* -------- 6. TEMPLATE SCOPE -------- */}
          <section className="customize-table-modal__section">
            <h3 className="customize-table-modal__section-header capitalize">
              {t(
                "challengeReport.customize.sectionScope",
                "PROGRAM/TEAM FILTER"
              )}
              <span className="customize-table-modal__optional text-xssm font-normal opacity-60">
                {" "}
                ({t("challengeReport.customize.optional", "Optional")})
              </span>
            </h3>
            <p className="customize-table-modal__section-desc">
              {t(
                "challengeReport.customize.scopeDesc",
                "Limit the results of this template to a specific Program or Team Program. Leave blank to account for all users."
              )}
            </p>
            {programOptions.length > 0 ? (
              <ProgramMultiSelect
                options={programOptions}
                selected={localScopeTeamPrograms}
                onToggle={toggleScopeProgram}
                placeholder={t(
                  "challengeReport.customize.scopeAddProgram",
                  "Add program..."
                )}
                allSelectedLabel={t(
                  "challengeReport.customize.scopeAllSelected",
                  "All programs selected"
                )}
              />
            ) : (
              <p className="customize-table-modal__empty">
                {t(
                  "challengeReport.customize.scopeNoneAvailable",
                  "No team programs available."
                )}
              </p>
            )}
          </section>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Footer                                                               */}
      {/* ------------------------------------------------------------------ */}
      <footer className="customize-table-modal__footer">
        <label
          className={`!flex items-center gap-2 mb-0! mr-auto${
            templateName.trim() ? "" : " customize-table-modal__label--disabled"
          }`}
          title={t(
            "challengeReport.customize.setAsDefaultHint",
            "Name the template to set it as default"
          )}
        >
          <input
            type="checkbox"
            checked={asDefault}
            onChange={() => setAsDefault((v) => !v)}
            disabled={!templateName.trim()}
            className="shrink-0"
          />
          {t(
            "challengeReport.customize.setAsDefaultForYou",
            "Set as my default template (for you only)"
          )}
        </label>
        <button
          type="button"
          data-button-type="secondary"
          onClick={onClose}
          data-action="cancel"
        >
          {t("challengeReport.customize.cancel", "Cancel")}
        </button>
        <button type="button" data-button-type="primary" onClick={handleSave}>
          {t("challengeReport.customize.applyAndSave", "Apply & save")}
        </button>
      </footer>
    </Modal>
  );
};

export default CustomizeTableModal;
